/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    makeStyles,
} from '@fluentui/react-components';
import { ContactCardGroup24Regular, ContactCardGroupRegular } from '@fluentui/react-icons';
import { Column } from '../common/Column';
import { Row } from '../common/Row';
import { PartyControl } from '../controls/PartyControl';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { AddPartyDialog } from './AddPartyDialog';
import { useGuid } from '../../utilities/useGuid';
import { useToasts } from '../toasts/useToasts';
import { Party, PartyWithContacts } from '../../data/types';
import { usePartyService } from '../../data/services/usePartyService';

const useStyles = makeStyles({
    root: {},
    field: {
        width: '100%',
    },
    stateZip: {
        width: '125px',
    },
    addContactBtn: {
        fontSize: '13px',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
    party: {
        width: '16rem',
    },
});

export type AddAffiliatedPartiesDialogProps = {
    onSubmit: (parties: Party[]) => void;
    affiliatedParties?: PartyWithContacts[];
    retainedByPartyId?: string;
    children?: ReactElement<any>;
};
export const AddAffiliatedPartiesDialog = (props: AddAffiliatedPartiesDialogProps) => {
    const { onSubmit, affiliatedParties, retainedByPartyId, children } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const guid = useGuid();
    const toasts = useToasts();
    const partyService = usePartyService();
    const parties = partyService.parties;

    //State
    const [selectedParties, setSelectedParties] = useState<Party[]>([]);

    //Styles
    const styles = useStyles();

    //Logic
    const onPartySelected = (party: Party, selected: boolean) => {
        if (!!selected) {
            setSelectedParties((prev) => {
                return !!prev ? [...prev, party] : [party];
            });
        } else {
            setSelectedParties((prev) => {
                return prev.filter((p) => p.partyId != party.partyId);
            });
        }
    };
    const filterParties = (party: Party) => {
        return party.partyId != retainedByPartyId && !affiliatedParties?.some((p) => p.party?.partyId == party.partyId);
    };
    const onCreateNewParty = async (newParty: Party) => {
        newParty.accountId = primaryAccount?.account.accountId;
        newParty.partyId = guid.newGuid();

        try {
            await partyService.upsertPartyAsync(newParty);
            toasts.parties.notifyPartyCreated(newParty);
        } catch {
            toasts.parties.notifyPartyCreateError(newParty);
        }
    };

    //UI Events
    const onClickSubmit = (e: any) => {
        e.stopPropagation();

        onSubmit(selectedParties);
        clearSelectedParties();
    };
    const clearSelectedParties = (e?: any) => {
        !!e && e.stopPropagation();

        setSelectedParties([]);
    };

    //Effects
    useEffect(() => {
        return () => {
            setSelectedParties([]);
        };
    }, []);

    return (
        <Dialog>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined} style={{ maxWidth: '725px' }}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <ContactCardGroupRegular />
                            </Column>
                            <Column>Add Affiliated Party</Column>
                            <Column width='auto'>
                                <AddPartyDialog onSubmit={onCreateNewParty}>
                                    <Button
                                        className={styles.addContactBtn}
                                        icon={<ContactCardGroup24Regular />}
                                        appearance='transparent'
                                        data-testid='affiliated-party-dialog-add-new-party-button'
                                    >
                                        Add Party
                                    </Button>
                                </AddPartyDialog>
                            </Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column scroll maxHeight='500px' padding='py-3 px-2' align='center'>
                            <Row gap='medium' width='fit-content'>
                                {parties?.filter(filterParties).map((party) => (
                                    <PartyControl
                                        key={party?.partyId}
                                        className={styles.party}
                                        selectable
                                        onSelectionChange={onPartySelected}
                                        size='small'
                                        party={party}
                                    />
                                ))}
                            </Row>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                type='submit'
                                appearance='primary'
                                onClick={onClickSubmit}
                                disabled={!selectedParties.length}
                                data-testid='affiliated-party-dialog-add-party-button'
                            >
                                Add Affiliated Party
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance='secondary'
                                onClick={clearSelectedParties}
                                data-testid='affiliated-party-dialog-cancel-button'
                            >
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
